import type { RefreshFailureKind } from '../utils/matrixTokenRefresh';

export type SanitizedMatrixError = {
  errcode?: string;
  http_status?: string;
  failure_kind: RefreshFailureKind | 'rate_limit';
  /** Error constructor / `name` (e.g. `MatrixError`, `Error`) — safe, no user data. */
  error_name?: string;
  /** Truncated error message for Grafana triage. SDK crypto/auth messages carry no PIN or key material. */
  error_message?: string;
};

/** Max characters of an error message kept for telemetry — enough to triage, short enough to stay safe. */
const MAX_ERROR_MESSAGE_LENGTH = 160;

const asMatrixError = (
  err: unknown
): { httpStatus?: number; errcode?: string; message?: string; name?: string; data?: { errcode?: string } } => {
  if (!err || typeof err !== 'object') return {};
  const e = err as {
    httpStatus?: number;
    status?: number;
    errcode?: string;
    message?: string;
    name?: string;
    data?: { errcode?: string };
  };
  return {
    httpStatus: typeof e.httpStatus === 'number' ? e.httpStatus : typeof e.status === 'number' ? e.status : undefined,
    errcode:
      typeof e.errcode === 'string' ? e.errcode : typeof e.data?.errcode === 'string' ? e.data.errcode : undefined,
    message: typeof e.message === 'string' ? e.message : undefined,
    name: typeof e.name === 'string' ? e.name : undefined,
  };
};

/** Strip a Matrix/HTTP error down to safe telemetry fields — never the full message. */
export const sanitizeMatrixError = (err: unknown): SanitizedMatrixError => {
  const { httpStatus, errcode, message, name } = asMatrixError(err);

  const errorFields = {
    error_name: name,
    error_message: message ? message.slice(0, MAX_ERROR_MESSAGE_LENGTH) : undefined,
  };

  if (httpStatus === 401 || errcode === 'M_UNKNOWN_TOKEN' || message?.includes('M_UNKNOWN_TOKEN')) {
    return {
      errcode: errcode ?? 'M_UNKNOWN_TOKEN',
      http_status: httpStatus !== undefined ? String(httpStatus) : undefined,
      failure_kind: 'permanent',
      ...errorFields,
    };
  }

  if (httpStatus === 429 || errcode === 'M_LIMIT_EXCEEDED') {
    return {
      errcode: errcode ?? 'M_LIMIT_EXCEEDED',
      http_status: String(httpStatus ?? 429),
      failure_kind: 'rate_limit',
      ...errorFields,
    };
  }

  return {
    errcode,
    http_status: httpStatus !== undefined ? String(httpStatus) : undefined,
    failure_kind: 'transient',
    ...errorFields,
  };
};
